import { APARTMENT_FURNITURE_OBJECT_KINDS } from './entityKinds';

export type FurnitureLayerMode = 'body' | 'details' | 'tech';
export type FurnitureRenderMode = 'solid' | 'flat' | 'wireframe' | 'xray';
type FurnitureRenderModeInput = FurnitureRenderMode | string | null | undefined;

export type FurnitureVisibilityMeta = {
  type?: unknown;
  moduleId?: unknown;
  render?: Record<string, unknown>;
  domain?: Record<string, unknown>;
};

function cleanRole(value: unknown): string {
  return String(value ?? '').trim().toLowerCase();
}

export function normalizeFurnitureRenderMode(mode: FurnitureRenderModeInput): FurnitureRenderMode {
  let cleaned = '';
  const raw = String(mode ?? 'solid').trim().toLowerCase();
  for (let i = 0; i < raw.length; i += 1) {
    const char = raw[i];
    if (char !== ' ' && char !== '\t' && char !== '\n' && char !== '_' && char !== '-') cleaned += char;
  }
  if (cleaned === 'wire' || cleaned === 'wireframe') return 'wireframe';
  if (cleaned === 'xray' || cleaned === 'xraymode' || cleaned === 'x') return 'xray';
  if (cleaned === 'flat') return 'flat';
  return 'solid';
}

function renderModeList(value: unknown): FurnitureRenderMode[] {
  if (!Array.isArray(value)) return [];
  return value.map((mode) => normalizeFurnitureRenderMode(String(mode))).filter(Boolean);
}

function finiteNumber(value: unknown): number | null {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
}

export type FurnitureRenderModeMaterialState = {
  opacity: number;
  transparent: boolean;
  depthWrite: boolean;
  wireframe: boolean;
};

/**
 * Object-owned visibility policy for a furniture scene entity.
 *
 * Render modes and UI layers are just inputs. The renderer does not classify
 * hardware by labels: hidden/internal behavior must come from the entity's own
 * visibilityContract.
 */
export class FurnitureEntityVisibility {
  readonly meta: FurnitureVisibilityMeta;

  constructor(meta: FurnitureVisibilityMeta | null | undefined) {
    this.meta = meta ?? {};
  }

  private get moduleId(): string {
    return cleanRole(this.meta.moduleId);
  }

  private get domain(): Record<string, unknown> {
    const domain = this.meta.domain;
    return domain && typeof domain === 'object' ? domain : {};
  }

  private get objectKind(): string {
    const domain = this.domain;
    return cleanRole(domain.objectKind);
  }

  isSupersededPro100Source(): boolean {
    const replacement = this.domain.activeCatalogReplacement;
    return Boolean(replacement && typeof replacement === 'object' && !Array.isArray(replacement));
  }

  private visibilityContract(): Record<string, unknown> | null {
    const contract = this.domain.visibilityContract;
    return contract && typeof contract === 'object' ? contract as Record<string, unknown> : null;
  }

  private renderModeContract(): Record<string, unknown> {
    const contract = this.domain.renderModeContract;
    return contract && typeof contract === 'object' ? contract as Record<string, unknown> : {};
  }

  private isDrawerMotionFollower(): boolean {
    const domain = this.domain;
    const assembly = domain.drawerAssembly;
    if (assembly && typeof assembly === 'object') return true;
    if (this.objectKind === 'drawer_box') return true;
    const hardwareClass = cleanRole(domain.hardwareClass);
    return hardwareClass === 'drawer_slide'
      || hardwareClass === 'drawer_carga'
      || hardwareClass === 'drawer_railing'
      || hardwareClass === 'drawer_tip_on'
      || hardwareClass === 'servo_drive';
  }

  materialStateForMode(renderMode: FurnitureRenderModeInput): FurnitureRenderModeMaterialState {
    const mode = normalizeFurnitureRenderMode(renderMode);
    const contract = this.renderModeContract();
    const perMode = contract[mode];
    const modeContract = perMode && typeof perMode === 'object' ? perMode as Record<string, unknown> : {};
    const opacity =
      finiteNumber(modeContract.opacity)
      ?? finiteNumber(contract.opacity)
      ?? 1;
    const transparent =
      typeof modeContract.transparent === 'boolean'
        ? modeContract.transparent
        : typeof contract.transparent === 'boolean'
          ? contract.transparent
          : opacity < 1;
    const depthWrite =
      typeof modeContract.depthWrite === 'boolean'
        ? modeContract.depthWrite
        : typeof contract.depthWrite === 'boolean'
          ? contract.depthWrite
          : opacity >= 1;
    const wireframe =
      typeof modeContract.wireframe === 'boolean'
        ? modeContract.wireframe
        : typeof contract.wireframe === 'boolean'
          ? contract.wireframe
          : false;
    return {
      opacity: Math.max(0, Math.min(1, opacity)),
      transparent,
      depthWrite,
      wireframe,
    };
  }

  isInspectionReadableHardware(): boolean {
    if (this.objectKind !== 'hardware' && this.objectKind !== 'module_hardware') return false;
    return true;
  }

  isApartmentShell(): boolean {
    return this.objectKind === 'apartment_shell';
  }

  isApartmentTech(): boolean {
    return this.objectKind === 'apartment_tech';
  }

  isApartmentFurniture(): boolean {
    return APARTMENT_FURNITURE_OBJECT_KINDS.has(this.objectKind);
  }

  renderPolicyVisibility(
    renderMode: FurnitureRenderModeInput,
    openedPanels: Record<string, number> = {},
  ): boolean | null {
    const mode = normalizeFurnitureRenderMode(renderMode);
    const visibilityContract = this.visibilityContract();
    if (visibilityContract) {
      const ownerId = String(visibilityContract.ownerId || '');
      const ownerOpen = Boolean(openedPanels.__all || (ownerId && (openedPanels[ownerId] || 0) > 0.01));
      if (ownerOpen && visibilityContract.visibleWhenOwnerOpen !== false) return true;
      const contractModes = renderModeList(visibilityContract.visibleInModes);
      // Closed drawer followers stay hidden in normal and wireframe views.
      // X-Ray reveals only real members explicitly published for inspection;
      // service/aggregate hardware is not drawable geometry.
      if (ownerId && this.isDrawerMotionFollower()) {
        return mode === 'xray'
          && contractModes.includes('xray')
          && visibilityContract.inspectorKind !== 'service';
      }
      if (contractModes.length > 0) return contractModes.includes(mode);
      if (visibilityContract.defaultVisible === false) return false;
      if (visibilityContract.defaultVisible === true) return true;
    }
    return null;
  }

  shouldShow(
    layerMode: FurnitureLayerMode,
    renderMode: FurnitureRenderModeInput = 'solid',
    openedPanels: Record<string, number> = {},
  ): boolean {
    if (this.isSupersededPro100Source()) return false;
    const mode = normalizeFurnitureRenderMode(renderMode);
    const policyVisibility = this.renderPolicyVisibility(mode, openedPanels);
    if (policyVisibility !== null) return policyVisibility;
    if (layerMode === 'tech') return true;
    const isHelper = this.objectKind === 'service' || this.objectKind === 'helper';
    if (isHelper) return false;
    const isInspectionMode = mode === 'wireframe' || mode === 'xray';
    if (isInspectionMode) return true;
    if (layerMode === 'body') return true;
    return true;
  }

  shouldShowApartmentLayer(layerMode: FurnitureLayerMode): boolean {
    if (this.isSupersededPro100Source()) return false;
    if (this.objectKind === 'apartment_ceiling') return false;
    if (layerMode === 'tech') return true;
    if (layerMode === 'body') return this.isApartmentShell() || this.isApartmentFurniture();
    if (this.isApartmentShell() || this.isApartmentTech()) return false;
    return this.isApartmentFurniture();
  }

  shouldShowSceneLayer(
    layerMode: FurnitureLayerMode,
    renderMode: FurnitureRenderModeInput = 'solid',
    openedPanels: Record<string, number> = {},
    options: { apartmentScene?: boolean } = {},
  ): boolean {
    if (this.isSupersededPro100Source()) return false;
    // Нераспознанная фурнитура исходника исключена из рендера во всех режимах
    // и слоях — публикация убрала её из сцены, а не спрятала на время.
    if (unrecognizedSourceFurnitureDomain(this.domain)) return false;
    const mode = normalizeFurnitureRenderMode(renderMode);
    const policyVisibility = this.renderPolicyVisibility(mode, openedPanels);
    if (policyVisibility !== null) return policyVisibility;
    if (options.apartmentScene) return this.shouldShowApartmentLayer(layerMode);
    return this.shouldShow(layerMode, mode, openedPanels);
  }
}

export function initialFurnitureObjectVisible(meta: FurnitureVisibilityMeta | null | undefined): boolean {
  const visibility = new FurnitureEntityVisibility(meta);
  if (visibility.isSupersededPro100Source()) return false;
  if (unrecognizedSourceFurnitureDomain(
    meta?.domain && typeof meta.domain === 'object' ? meta.domain as Record<string, unknown> : {},
  )) {
    return false;
  }
  return visibility.renderPolicyVisibility('solid') ?? true;
}

export const UNRECOGNIZED_SOURCE_HARDWARE_REASON = 'unrecognized_source_hardware';

/**
 * Нераспознанная фурнитура исходника (публикация SKP) не рендерится.
 *
 * Политика владельца: сцена опубликованной SKP-модели — без мусора. Меши
 * исходника на слоях «фурнитура: …», не принятые в производство, исключены
 * публикацией из рендер-сцены; распознанная фурнитура, панели и деньги
 * (сметные строки) не затронуты. Решение живёт в домене сущности:
 * - новые публикации несут штамп `visibilityContract` с причиной
 *   `unrecognized_source_hardware`;
 * - бандлы, опубликованные до штампа, выводят то же самое из фактов
 *   источника: `manufacturingExcluded` + слой семейства «фурнитура:».
 */
export function unrecognizedSourceFurnitureDomain(domain: Record<string, unknown> | null | undefined): boolean {
  if (!domain) return false;
  const contract = domain.visibilityContract;
  if (contract && typeof contract === 'object' && !Array.isArray(contract)) {
    const visibilityContract = contract as Record<string, unknown>;
    if (visibilityContract.defaultVisible === false) {
      // Чужие defaultVisible:false (например, последователи ящиков) остаются
      // за своими владельцами — исключение узнаётся по своей причине.
      return visibilityContract.reason === UNRECOGNIZED_SOURCE_HARDWARE_REASON;
    }
    return false;
  }
  if (domain.manufacturingExcluded !== true) return false;
  return String(domain.sourceLayer ?? '').trim().toLowerCase().startsWith('фурнитура:');
}
